










































































































import { Vue, Component } from "vue-property-decorator";
import CustomerService from "@/services/customer_service";
import { Customer } from "@/models/customer/customer";
import { makeTitleCase } from "@/utility/helpers";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderActions, OrderGetters } from "@/store/modules/order/types";
import { LineItem, Order } from "@/models/order/order";
import ConfirmNewQuote from "@/components/elements/modal/ConfirmNewQuote.vue";
import ConfirmClearCustomer from "@/components/elements/modal/ConfirmClearCustomer.vue";
import AssignSavedQuote from "@/components/elements/modal/AssignSavedQuote.vue";
import { GlobalActions } from "@/store/modules/global/types";
import { SalesRegions } from "@/models/customer/customer_helpers";
import ContactUs from "../elements/modal/ContactUs.vue";

@Component({
  components: {
    ConfirmNewQuote,
    ConfirmClearCustomer,
    AssignSavedQuote,
    ContactUs
  }
})
export default class CustomerDropdown extends Vue {
  @(namespace(StoreModule.Order).Action(OrderActions.AssignCustomer))
  assignCustomer!: (value: {
    customer_id: string;
    customer_name: string;
    order_id: string | null;
  }) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetAssignedCustomer))
  customer!: string;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplier!: (value: number) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ClearOrder))
  resetOrder!: () => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  items!: LineItem[];
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  order!: Order;

  protected selected: {
    customer_id: string;
    customer_name: string;
    multiplier: number;
  } | null = null;
  private searchTerm = "";
  protected makeTitleCase = makeTitleCase;
  protected fields = [
    "customer_name",
    "store_name",
    "phone",
    "address",
    "location",
    "website_url"
  ];
  protected customerService = new CustomerService();
  protected customers: Customer[] | null = null;
  protected showModal = false;
  protected showModalClearCustomer = false;
  protected showModalAssignCustomer = false;
  protected showContactUsModal = false;
  protected newCustomer: Customer | null = null;

  async created() {
    this.customers = await this.customerService.getCurrentCustomers();
    this.newCustomer = this.getNewCustomerInfo();
  }

  protected getNewCustomerInfo(): Customer | null {
    if (this.customers === null) return null;
    const newCustomerArr = this.customers.filter((c: Customer) => {
      return c.customer_name === "New Customer" &&
      c.hide_from_map &&
      c.sales_region === SalesRegions.Unassigned
    });
    return newCustomerArr[0];
  }

  protected toggleContactUsModal() {
    this.showContactUsModal = !this.showContactUsModal;
  }

  protected formatHeaders(header: string): string {
    let formatted = "";
    switch (header) {
      case "store_name":
        formatted = "Company Name";
        break;
      case "sales_contacts":
        formatted = "Contact";
        break;
      default:
        formatted = this.makeTitleCase(header);
        break;
    }
    return formatted;
  }

  /** Remove unneeded fields from fetched data & perform search before returning data to view */
  get filteredCustomers() {
    if (this.customers) {
      this.customers = this.customers.map(c => {
        c.sales_region = this.makeTitleCase(c.sales_region) as any;
        return c;
      });
      if (this.searchTerm != "") {
        const filter = (this.customers as any[]).filter(item => {
          return (
            item.customer_name
              .toLowerCase()
              .indexOf(this.searchTerm.toLowerCase()) !== -1
          );
        });
        return filter;
      } else {
        return this.customers;
      }
    } else {
      return [];
    }
  }

  protected async clearOrder(): Promise<void> {
    await this.resetOrder();
    await this.reassignQuote(
      this.selected?.customer_id!,
      this.selected?.customer_name!
    );
    await this.addMultiplier(this.selected?.multiplier!);
  }

  protected toggleNewQuoteModal(
    customerId: string,
    customerName: string,
    customerMultiplier: number
  ): void {
    this.selected = {
      customer_id: customerId,
      customer_name: customerName,
      multiplier: customerMultiplier
    };
    if (this.order._id) {
      this.showModalAssignCustomer = true;
    } else {
      if (
        this.items.length > 0 &&
        !(this.selected.customer_name === "New Customer" && this.customer === "New Customer")
      ) {
        // only toggling warning modal if there is an item associated with the order
        this.showModal = true;
      } else {
        this.reassignQuote(
          this.selected?.customer_id!,
          this.selected?.customer_name!
        );
      }
    }
  }

  protected toggleClearCustomer(): void {
    this.showModalClearCustomer = true;
  }

  protected async assignSavedQuote() {
    const id = this.selected?.customer_id!;
    const name = this.selected?.customer_name!;
    const multiplier = this.selected?.multiplier!;
    const oid = this.order._id;
    await this.assignCustomer({
      customer_id: id,
      customer_name: name,
      order_id: oid
    });
    this.addMultiplier(multiplier);
    this.close(); // sets selected to null, must be called last
  }

  protected async reassignQuote(
    customerId?: string,
    customerName?: string,
    orderId?: string
  ) {
    const id = customerId ? customerId : this.selected?.customer_id!;
    const name = customerName ? customerName : this.selected?.customer_name!;
    const multiplier = this.selected?.multiplier!;
    const oid = orderId ? orderId : null;
    await this.assignCustomer({
      customer_id: id,
      customer_name: name,
      order_id: oid
    });
    this.addMultiplier(multiplier);
    this.close(); // sets selected to null, must be called last
  }

  protected close(): void {
    this.showModal = false;
    this.showModalClearCustomer = false;
    this.showModalAssignCustomer = false;
    this.selected = null;
  }
}
